/**
 * O estado de cada recurso do apply, derivado do plano (`acoes`) e das linhas
 * do log que `useAndamentoDoPedido` acumula (spec 2026-10-01-acompanhamento-
 * do-apply-por-recurso D1-D4). Puro: o tempo é sempre o texto do Terraform.
 */
export type EstadoDoRecurso = 'aguardando' | 'em_curso' | 'feito' | 'erro' | 'interrompido'
export type Acao = 'criar' | 'alterar' | 'destruir' | 'recriar'
export type Categoria = 'banco' | 'instancia' | 'balanceador' | 'dns' | 'rede' | 'permissao'
  | 'funcao' | 'segredo' | 'armazenamento' | 'certificado' | 'outros'

export interface CategoriaDef { chave: Categoria; rotulo: string; prefixos: string[] }

export interface RecursoDoApply {
  endereco: string
  tipo: string
  categoria: Categoria
  acao: Acao
  estado: EstadoDoRecurso
  decorrido: string | null
  erro: string | null
}

// A ORDEM decide (D3): o primeiro prefixo que casa vence, e `aws_route53_`
// tem de vir antes de `aws_route`.
export const CATEGORIAS: CategoriaDef[] = [
  { chave: 'banco', rotulo: 'Banco', prefixos: ['aws_db_', 'aws_rds_', 'aws_elasticache_'] },
  { chave: 'instancia', rotulo: 'Instância', prefixos: ['aws_instance', 'aws_autoscaling_', 'aws_launch_template'] },
  { chave: 'balanceador', rotulo: 'Balanceador', prefixos: ['aws_lb', 'aws_alb'] },
  { chave: 'dns', rotulo: 'DNS', prefixos: ['aws_route53_'] },
  { chave: 'rede', rotulo: 'Rede', prefixos: ['aws_vpc', 'aws_subnet', 'aws_route', 'aws_nat_',
    'aws_internet_gateway', 'aws_eip', 'aws_ec2_transit_gateway', 'aws_security_group'] },
  { chave: 'permissao', rotulo: 'Permissão', prefixos: ['aws_iam_'] },
  { chave: 'funcao', rotulo: 'Função', prefixos: ['aws_lambda_'] },
  { chave: 'segredo', rotulo: 'Segredo', prefixos: ['aws_secretsmanager_', 'aws_kms_', 'aws_ssm_parameter'] },
  { chave: 'armazenamento', rotulo: 'Armazenamento', prefixos: ['aws_s3_', 'aws_ebs_', 'aws_efs_'] },
  { chave: 'certificado', rotulo: 'Certificado', prefixos: ['aws_acm_'] },
  { chave: 'outros', rotulo: 'Outros', prefixos: [] },
]

export function tipoDoEndereco(endereco: string): string {
  const partes = endereco.replace(/\[[^\]]*\]/g, '').split('.')
  let i = 0
  while (partes[i] === 'module') i += 2
  if (partes[i] === 'data') i += 1
  return partes[i] ?? endereco
}

export function categoriaDoTipo(tipo: string): Categoria {
  for (const c of CATEGORIAS) {
    if (c.prefixos.some((p) => tipo.startsWith(p))) return c.chave
  }
  return 'outros'
}

function acaoDe(acoes: string[] | undefined): Acao {
  const s = new Set(acoes ?? ['create'])
  if (s.has('delete') && s.has('create')) return 'recriar'
  if (s.has('delete')) return 'destruir'
  if (s.has('update')) return 'alterar'
  return 'criar'
}

// O endereço é tudo antes do PRIMEIRO ": " seguido de um verbo do Terraform —
// a chave pode ter ":" dentro, então ancorar no verbo, não no separador.
const _INICIO = /^(.+?): (?:Creating|Modifying|Destroying)\.\.\.$/
const _AINDA = /^(.+?): Still (?:creating|modifying|destroying)\.\.\. \[([^\]]*?) ?elapsed\]$/
const _FIM = /^(.+?): (Creation|Modifications|Destruction) complete after (\S+)$/
const _ERRO = /^Error: /
const _WITH = /^(?:│ )?\s*with (.+),$/

export function recursosDoApply(
  acoes: Record<string, string[]>, linhas: string[],
): { recursos: RecursoDoApply[]; erroSolto: string | null } {
  const mapa = new Map<string, RecursoDoApply>()
  const garantir = (endereco: string): RecursoDoApply => {
    let r = mapa.get(endereco)
    if (!r) {
      const tipo = tipoDoEndereco(endereco)
      r = { endereco, tipo, categoria: categoriaDoTipo(tipo), acao: acaoDe(acoes[endereco]),
            estado: 'aguardando', decorrido: null, erro: null }
      mapa.set(endereco, r)
    }
    return r
  }
  Object.keys(acoes).forEach(garantir)

  const errosPendentes: string[] = []
  let ultimoErro: string | null = null
  for (const linha of linhas) {
    let m: RegExpExecArray | null
    if ((m = _INICIO.exec(linha))) {
      const r = garantir(m[1]); r.estado = 'em_curso'; r.decorrido = null
    } else if ((m = _AINDA.exec(linha))) {
      const r = garantir(m[1]); r.estado = 'em_curso'; r.decorrido = m[2]
    } else if ((m = _FIM.exec(linha))) {
      const r = garantir(m[1])
      // Replace: a destruição concluída ainda não é o fim do item (D1a).
      if (r.acao === 'recriar' && m[2] === 'Destruction') { r.estado = 'em_curso'; r.decorrido = null }
      else { r.estado = 'feito'; r.decorrido = m[3] }
    } else if (_ERRO.test(linha)) {
      ultimoErro = linha
      errosPendentes.push(linha)
    } else if ((m = _WITH.exec(linha)) && ultimoErro) {
      const r = garantir(m[1]); r.estado = 'erro'; r.erro = ultimoErro
      errosPendentes.splice(errosPendentes.indexOf(ultimoErro), 1)
      ultimoErro = null
    }
  }

  // D4 v1: o erro sem `with` vai para o ÚNICO em curso; com vários ou nenhum,
  // fica solto e quem estava em curso vira "interrompido" — atribuir seria chute.
  let erroSolto: string | null = null
  if (errosPendentes.length > 0) {
    const emCurso = [...mapa.values()].filter((r) => r.estado === 'em_curso')
    if (emCurso.length === 1 && errosPendentes.length === 1) {
      emCurso[0].estado = 'erro'; emCurso[0].erro = errosPendentes[0]
    } else {
      erroSolto = errosPendentes.join('\n')
      emCurso.forEach((r) => { r.estado = 'interrompido' })
    }
  } else if ([...mapa.values()].some((r) => r.estado === 'erro')) {
    // Erro atribuído por `with`: o que ainda estava em curso foi interrompido.
    mapa.forEach((r) => { if (r.estado === 'em_curso') r.estado = 'interrompido' })
  }
  return { recursos: [...mapa.values()], erroSolto }
}
